// src/components/GoogleMapsImportModal.jsx
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useUser, API_BASE } from "../context/UserContext";

const C = {
  primary:    "#655d54",
  primaryDim: "#595149",
  bg:         "#faf9f6",
  container:  "#f4f4f0",
  containerHigh: "#edeeea",
  onSurface:  "#2f3430",
  variant:    "#5c605c",
  outline:    "#8a8e8a",
  outlineFaint: "rgba(101,93,84,0.08)",
  error:      "#9e422c",
  success:    "#1D9E75",
};
const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";

export default function GoogleMapsImportModal({ onClose, onDone, personalPlaces = [], initialUrl = "" }) {
  const { t } = useTranslation();
  const { user } = useUser();
  const [url, setUrl] = useState(initialUrl);
  const [phase, setPhase] = useState("input"); // input | review | importing | done
  const autoParseRef = useRef(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState("");
  const [listName, setListName] = useState("");
  const [places, setPlaces] = useState([]);
  const [existingSet, setExistingSet] = useState(new Set()); // indices of already-saved places
  const [selected, setSelected] = useState(new Set());
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importedCount, setImportedCount] = useState(0);
  const abortRef = useRef(false);

  const isMobile = window.innerWidth <= 768;

  // Auto-parse if opened with a URL
  useEffect(() => {
    if (initialUrl && !autoParseRef.current) {
      autoParseRef.current = true;
      handleParse(initialUrl);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Build lookup sets from user's existing places
  const existingGids = useRef(new Set());
  const existingNames = useRef(new Set());
  useEffect(() => {
    const gids = new Set();
    const names = new Set();
    for (const p of personalPlaces) {
      if (p.google_place_id) gids.add(p.google_place_id);
      names.add((p.name || "").toLowerCase().trim());
    }
    existingGids.current = gids;
    existingNames.current = names;
  }, [personalPlaces]);

  const isAlreadySaved = (place) => {
    if (place.google_place_id && existingGids.current.has(place.google_place_id)) return true;
    if (existingNames.current.has((place.name || "").toLowerCase().trim())) return true;
    return false;
  };

  const handleParse = async (overrideUrl) => {
    let trimmed = (typeof overrideUrl === "string" ? overrideUrl : url).trim();
    if (!trimmed || (!trimmed.includes("google") && !trimmed.includes("goo.gl") && !trimmed.includes("maps"))) {
      setError(t("import.invalid_url"));
      return;
    }
    setError("");
    setParsing(true);
    try {
      const resp = await axios.post(`${API_BASE}/google-import/parse-list`, { url: trimmed });
      const data = resp.data;
      if (!data.places || data.places.length === 0) {
        setError(t("import.no_places"));
        setParsing(false);
        return;
      }
      setListName(data.list_name);
      setPlaces(data.places);

      // Mark existing and auto-select only new places
      const existing = new Set();
      const selectable = new Set();
      data.places.forEach((p, i) => {
        if (isAlreadySaved(p)) {
          existing.add(i);
        } else {
          selectable.add(i);
        }
      });
      setExistingSet(existing);
      setSelected(selectable);
      setPhase("review");
    } catch (e) {
      setError(t("import.error"));
    }
    setParsing(false);
  };

  const newCount = places.length - existingSet.size;

  const toggleSelect = (idx) => {
    if (existingSet.has(idx)) return; // can't toggle already-saved
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === newCount) {
      setSelected(new Set());
    } else {
      const all = new Set();
      places.forEach((_, i) => { if (!existingSet.has(i)) all.add(i); });
      setSelected(all);
    }
  };

  const handleImport = async () => {
    const toImport = places.filter((_, i) => selected.has(i));
    if (toImport.length === 0) return;
    setPhase("importing");
    setImportProgress({ current: 0, total: toImport.length });
    abortRef.current = false;
    let count = 0;

    for (let i = 0; i < toImport.length; i++) {
      if (abortRef.current) break;
      const p = toImport[i];
      try {
        await axios.post(`${API_BASE}/personal-places/`, {
          name: p.name,
          address: p.address,
          lat: p.lat,
          lng: p.lng,
          category: p.category || null,
          google_place_id: p.google_place_id || null,
          status: "want_to_go",
          is_public: true,
        });
        count++;
      } catch {
        // skip errors
      }
      setImportProgress({ current: i + 1, total: toImport.length });
    }

    setImportedCount(count);
    setPhase("done");
  };

  const modal = (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: isMobile ? "100%" : 440,
          maxHeight: isMobile ? "85vh" : "80vh",
          background: C.bg,
          borderRadius: isMobile ? "18px 18px 0 0" : 18,
          display: "flex", flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "18px 20px 14px",
          borderBottom: `1px solid ${C.outlineFaint}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <h3 style={{ margin: 0, fontFamily: FH, fontSize: 18, fontWeight: 700, color: C.onSurface }}>
            {t("import.title")}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: 20, color: C.outline, padding: 4,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 22 }}>close</span>
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {/* Input phase */}
          {phase === "input" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ fontFamily: FL, fontSize: 14, color: C.variant, margin: 0 }}>
                {t("import.paste_hint")}
              </p>
              <input
                type="url"
                value={url}
                onChange={(e) => { setUrl(e.target.value); setError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleParse()}
                placeholder="https://maps.app.goo.gl/..."
                style={{
                  width: "100%", padding: "11px 14px",
                  border: `1.5px solid ${error ? C.error : C.outlineFaint}`,
                  borderRadius: 10, background: C.container,
                  fontFamily: FL, fontSize: 14, color: C.onSurface,
                  outline: "none",
                }}
              />
              {error && (
                <p style={{ fontFamily: FL, fontSize: 13, color: C.error, margin: 0 }}>{error}</p>
              )}
              <button
                onClick={handleParse}
                disabled={parsing || !url.trim()}
                style={{
                  width: "100%", padding: "12px",
                  border: "none", borderRadius: 10,
                  background: parsing || !url.trim()
                    ? C.container
                    : `linear-gradient(to bottom, ${C.primary}, ${C.primaryDim})`,
                  color: parsing || !url.trim() ? C.outline : "#fff6ef",
                  fontFamily: FL, fontSize: 14, fontWeight: 700,
                  cursor: parsing || !url.trim() ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                {parsing ? (
                  <>{t("import.parsing")}</>
                ) : (
                  <>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>travel_explore</span>
                    {t("import.parse")}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Review phase */}
          {phase === "review" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <p style={{ fontFamily: FL, fontSize: 14, fontWeight: 600, color: C.onSurface, margin: 0 }}>
                  {listName && <span style={{ fontFamily: FH, fontStyle: "italic" }}>{listName}</span>}
                  {" "}{t("import.places_found", { count: places.length })}
                  {existingSet.size > 0 && (
                    <span style={{ fontFamily: FL, fontSize: 12, color: C.outline, marginLeft: 6 }}>
                      ({existingSet.size} {t("import.already_saved").toLowerCase()})
                    </span>
                  )}
                </p>
                {newCount > 0 && (
                  <button
                    onClick={toggleAll}
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      fontFamily: FL, fontSize: 12, fontWeight: 600, color: C.primary,
                    }}
                  >
                    {selected.size === newCount ? t("import.deselect_all") : t("import.select_all")}
                  </button>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {places.map((p, i) => {
                  const saved = existingSet.has(i);
                  return (
                    <div
                      key={i}
                      onClick={() => toggleSelect(i)}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "10px 12px", borderRadius: 10,
                        background: saved ? "transparent" : selected.has(i) ? C.container : "transparent",
                        cursor: saved ? "default" : "pointer",
                        opacity: saved ? 0.5 : 1,
                        transition: "background 0.15s",
                      }}
                    >
                      {saved ? (
                        <div style={{
                          width: 20, height: 20, borderRadius: 5,
                          background: C.success,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 14, color: "#fff" }}>check</span>
                        </div>
                      ) : (
                        <div style={{
                          width: 20, height: 20, borderRadius: 5,
                          border: selected.has(i) ? `2px solid ${C.primary}` : `2px solid ${C.outline}`,
                          background: selected.has(i) ? C.primary : "transparent",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}>
                          {selected.has(i) && (
                            <span className="material-symbols-outlined" style={{ fontSize: 14, color: "#fff" }}>check</span>
                          )}
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <p style={{
                            fontFamily: FL, fontSize: 14, fontWeight: 600,
                            color: C.onSurface, margin: 0,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {p.name}
                          </p>
                          {saved && (
                            <span style={{
                              fontFamily: FL, fontSize: 10, fontWeight: 700,
                              color: C.success, textTransform: "uppercase", letterSpacing: "0.05em",
                              flexShrink: 0,
                            }}>
                              {t("import.already_saved")}
                            </span>
                          )}
                        </div>
                        {p.address && (
                          <p style={{
                            fontFamily: FL, fontSize: 12, color: C.outline, margin: "2px 0 0",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {p.address}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Importing phase */}
          {phase === "importing" && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "30px 0" }}>
              <div style={{
                width: 48, height: 48, borderRadius: "50%",
                border: `3px solid ${C.container}`, borderTopColor: C.primary,
                animation: "spin 0.8s linear infinite",
              }} />
              <p style={{ fontFamily: FL, fontSize: 15, fontWeight: 600, color: C.onSurface, margin: 0 }}>
                {t("import.importing", { current: importProgress.current, total: importProgress.total })}
              </p>
              <div style={{
                width: "100%", height: 6, borderRadius: 3, background: C.container, overflow: "hidden",
              }}>
                <div style={{
                  height: "100%", borderRadius: 3, background: C.primary,
                  width: `${(importProgress.current / importProgress.total) * 100}%`,
                  transition: "width 0.3s",
                }} />
              </div>
              <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            </div>
          )}

          {/* Done phase */}
          {phase === "done" && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, padding: "30px 0" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 48, color: C.success }}>
                check_circle
              </span>
              <p style={{ fontFamily: FL, fontSize: 16, fontWeight: 700, color: C.onSurface, margin: 0 }}>
                {t("import.done", { count: importedCount })}
              </p>
              <button
                onClick={() => { onDone?.(); onClose(); }}
                style={{
                  marginTop: 8, padding: "11px 32px",
                  border: "none", borderRadius: 10,
                  background: `linear-gradient(to bottom, ${C.primary}, ${C.primaryDim})`,
                  color: "#fff6ef",
                  fontFamily: FL, fontSize: 14, fontWeight: 700, cursor: "pointer",
                }}
              >
                {t("import.close")}
              </button>
            </div>
          )}
        </div>

        {/* Footer - Import button (review phase only) */}
        {phase === "review" && selected.size > 0 && (
          <div style={{
            padding: "12px 20px 16px",
            borderTop: `1px solid ${C.outlineFaint}`,
          }}>
            <button
              onClick={handleImport}
              style={{
                width: "100%", padding: "12px",
                border: "none", borderRadius: 10,
                background: `linear-gradient(to bottom, ${C.primary}, ${C.primaryDim})`,
                color: "#fff6ef",
                fontFamily: FL, fontSize: 14, fontWeight: 700,
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>download</span>
              {t("import.import_selected")} ({selected.size})
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
